import React, { useState, useMemo, useEffect } from 'react'

import { LinkOutlined, QuestionCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { useImport } from '@hooks/shared/useImport'
import ImportModal from '@shared/ImportModal'
import useCan from '@shared/permissions/useCan'
import ProductBatchEditPanel from '@shared/table/ProductBatchEditPanel'
import { MultipleValue, SelectValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'
import TableIndex from '@shared/table/TableIndex'
import { Tag, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { productService } from '@services/admin/productService' // Added this import
import { productAttributeDefinitionService } from '@services/admin/productAttributeDefinitionService'
import { brandService as adminBrandService } from '@services/admin/brandService'
import { productCategoryService as adminProductCategoryService } from '@services/admin/productCategoryService'
import { productCategoryService as operatorProductCategoryService } from '@services/operator/productCategoryService'
import { downloadImportTemplate } from '@utils/downloadTemplate'

import type { Permission } from '@/permissions_manifest.generated'

import { formatDateTime, formatDate } from '../../../utils/dateTime'


interface StockData {
  total?: number;
  available?: number;
}

interface Merchant {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface ProductService {
  export: (ids: string[], language: string) => Promise<unknown>;
  import: (formData: FormData) => Promise<unknown>;
  [key: string]: any;
}

interface BrandService {
  fetchList: (params?: Record<string, unknown>) => Promise<{ data: Array<{ id: string; name: string }> }>;
  [key: string]: any;
}

interface ProductsListProps {
  role?: 'admin' | 'operator';
  service: ProductService;
  brandService?: BrandService;
  addJob?: (jobId: string, jobName: string) => void;
  merchants?: Merchant[];
  importModalVisible?: boolean;
  setImportModalVisible?: (visible: boolean) => void;
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
}

interface ProductAttributeDefinition {
  name: string;
  label: string;
  data_type: 'select' | 'boolean' | 'date' | 'text' | 'number' | string;
  options?: string[];
}

interface BatchAction {
  name: string;
  type?: string;
  className?: string;
  requiredPermission?: Permission;
  do: (selected: Iterable<string>) => void;
}

const ProductsList: React.FC<ProductsListProps> = ({
  role = 'admin',
  service,
  brandService: injectedBrandService,
  addJob,
  merchants = [],
  importModalVisible: externalImportModalVisible,
  setImportModalVisible: externalSetImportModalVisible,
  hideMerchantUI = false // Operator single-merchant 模式使用
}) => {
  // 內部 .jsx 子元件型別不完整（context-derived props 不會出現在頂層 destructure），
  // 因此一律 cast 成 ComponentType<any> 保留原 runtime 行為。
  // 放在 component body 內以避免 Vite HMR 下的 top-level TDZ。

  const currentBrandService = injectedBrandService || adminBrandService
  const currentCategoryService = role === 'operator' ? operatorProductCategoryService : adminProductCategoryService
  const { t, i18n } = useTranslation()
  const can = useCan()
  const [lastModified, setLastModified] = useState(0)
  const [showBatchEditPanel, setShowBatchEditPanel] = useState(false)
  const [productAttributeDefinitions, setProductAttributeDefinitions] = useState<ProductAttributeDefinition[]>([])
  const [internalImportModalVisible, setInternalImportModalVisible] = useState(false)

  // 支援外部或內部控制 import modal
  const importModalVisible = externalImportModalVisible !== undefined ? externalImportModalVisible : internalImportModalVisible
  const setImportModalVisible = externalSetImportModalVisible || setInternalImportModalVisible

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  // 載入自定義欄位定義（僅 Admin）
  useEffect(() => {
    if (role !== 'admin') return;

    const loadAttributeDefinitions = async () => {
      try {
        const result = await (productAttributeDefinitionService as any).fetchList()
        if (result.success) {
          setProductAttributeDefinitions(result.data || [])
        }
      } catch (error) {
        console.error('Failed to load product attribute definitions:', error)
      }
    }
    loadAttributeDefinitions()
  }, [role])

  // 使用共用 batch operation hook
  const executeBatchOperation = (useBatchOperation as any)(service, addJob)

  // 匯入功能（使用 Context 共享背景任務管理器）
  const handleImport = (useImport as any)({
    action: (formData: FormData) => service.import(formData),
    addJob,
    jobName: 'shared.products.list.import.job_name',
    i18nKeys: {
      noFile: 'shared.products.list.import.no_file',
      invalidFile: 'shared.products.list.import.invalid_file',
      success: 'shared.products.list.import.success',
      error: 'shared.products.list.import.error',
    }
    // 注意：不在 onSuccess 重新載入表格，因為 Background Job 還在執行中
    // 使用者需要在 Background Job 完成後手動重新整理，或等待自動通知
  })

  // 處理 Modal 匯入
  const handleModalImport = async (file: File, merchantId: string, overrideExisting: boolean) => {
    await handleImport(file, merchantId, overrideExisting)
  }

  // 處理下載範例
  const handleDownloadTemplate = async (merchantId?: string) => {
    await (downloadImportTemplate as any)({
      service,
      language: i18n.language,
      merchantId,
      t,
      successKey: 'shared.products.list.template.success',
      errorKey: 'shared.products.list.template.error'
    })
  }

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        nameKey: 'models.product.name',
        persist: true,
        sortable: true,
        sortFieldName: 'products.name',
        width: '200px',
        wrap: true,
        cell: ({ id, name }: { id: string; name: string }) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/products/${id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`${urlPrefix}/products/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      }
    ];

    // Add merchant column for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      cols.push({
        nameKey: 'shared.products.list.columns.merchant',
        wrap: true,
        selector: ({ merchant_id }: { merchant_id: string }) => {
          return merchants.find((merchant) => merchant.id === merchant_id)?.name
        },
      });
    }

    cols.push(
      {
        nameKey: 'models.product.sku',
        name: (
          <Space size={4}>
            {t('models.product.sku')}
            <Tooltip title={t('models.product.sku_tooltip')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: true,
        sortFieldName: 'products.sku',
        wrap: true,
        selector: ({ sku }: { sku: string }) => sku,
      },
      {
        nameKey: 'models.product.barcode',
        name: (
          <Space size={4}>
            {t('models.product.barcode')}
            <Tooltip title={t('models.product.barcode_tooltip')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: true,
        sortFieldName: 'products.barcode',
        wrap: true,
        selector: ({ barcode }: { barcode: string }) => barcode,
      },
      {
        nameKey: 'models.product.brand',
        name: (
          <Space size={4}>
            {t('models.product.brand')}
            <Tooltip title={t('models.product.brand_tooltip')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        wrap: true,
        selector: ({ brand }: { brand: string }) => brand,
      },
      {
        nameKey: 'models.product.last_inbound_date',
        sortable: true,
        sortFieldName: 'products.last_inbound_at',
        width: "120px",
        wrap: true,
        cell: ({ last_inbound_date }: { last_inbound_date: string | null }) => formatDate(last_inbound_date as string),
      },
      {
        nameKey: 'models.product.last_outbound_date',
        sortable: true,
        sortFieldName: 'products.last_shipped_at',
        width: "120px",
        wrap: true,
        cell: ({ last_outbound_date }: { last_outbound_date: string | null }) => formatDate(last_outbound_date as string),
      },
      {
        nameKey: 'models.product.created_at',
        name: (
          <Space size={4}>
            {t('models.product.created_at')}
            <Tooltip title={t('models.product.created_at_tooltip')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: true,
        sortFieldName: 'products.id',
        width: '180px',
        wrap: true,
        cell: ({ created_at }: { created_at: string | null }) => created_at ? formatDateTime(created_at) : '-',
      },
      {
        nameKey: 'models.product.activated',
        name: (
          <Space size={4}>
            {t('models.product.activated')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('models.product.activated_tooltip')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        width: "100px",
        cell: ({ activated }: { activated: boolean }) => {
          if (activated) {
            return (
              <Tag color="success" icon={<CheckOutlined />}>
                {t('shared.products.list.activated_status.active')}
              </Tag>
            )
          }
          return (
            <Tag color="default" icon={<CloseOutlined />}>
              {t('shared.products.list.activated_status.inactive')}
            </Tag>
          )

        }
      },
      {
        nameKey: 'models.product.available_quantity',
        name: (
          <Space size={4}>
            {t('models.product.available_quantity')}
            <Tooltip title={t('models.product.available_quantity_tooltip')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: "100px",
        wrap: true,
        cell: ({ stocks }: { stocks: StockData }) => stocks.available,
      },
      {
        nameKey: 'models.product.total_stock',
        name: (
          <Space size={4}>
            {t('models.product.total_stock')}
            <Tooltip title={t('models.product.total_stock_tooltip')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: "140px",
        wrap: true,
        cell: ({ stocks }: { stocks: StockData }) => stocks.total,
      }
    );

    return cols;
  }, [role, urlPrefix, merchants, hideMerchantUI, t])

  const supportRules = useMemo(() => {
    const rules: any[] = [
      {
        label: t(`models.product.sku`),
        component: MultipleValue,
        inputName: 'products.sku',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.products.list.filter_tooltips.sku.title'),
          content: t('shared.products.list.filter_tooltips.sku.content'),
        },
      },
      {
        label: t(`models.product.barcode`),
        component: MultipleValue,
        inputName: 'products.barcode',
        createable: true,
        tooltip: {
          title: t('shared.products.list.filter_tooltips.barcode.title'),
          content: t('shared.products.list.filter_tooltips.barcode.content'),
        },
      }
    ];

    // Add merchant filter for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      rules.push({
        label: t(`shared.products.list.filters.merchant`),
        component: MultipleValue,
        inputName: 'products.merchant_id',
        createable: false,
        defaultOptions: merchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
        tooltip: {
          title: t('shared.products.list.filter_tooltips.merchant.title'),
          content: t('shared.products.list.filter_tooltips.merchant.content'),
        },
      });
    }

    rules.push(
      {
        label: t(`models.product.other_barcode`),
        component: MultipleValue,
        inputName: 'product_barcodes.barcode',
        createable: true,
        tooltip: {
          title: t('shared.products.list.filter_tooltips.other_barcode.title'),
          content: t('shared.products.list.filter_tooltips.other_barcode.content'),
        },
      },
      {
        label: t(`models.product.name`),
        component: MultipleValue,
        inputName: 'products.name',
        createable: true,
        tooltip: {
          title: t('shared.products.list.filter_tooltips.name.title'),
          content: t('shared.products.list.filter_tooltips.name.content'),
        },
      },
      {
        label: t(`models.product.brand`),
        component: MultipleValue,
        inputName: 'products.brand_id',
        createable: false,
        lazyLoad: {
          action: (params: Record<string, unknown>) => currentBrandService.fetchList(params),
          transform: (data: { data: Array<{ id: string; name: string }> }) => data.data.map((brand) => ({
            label: brand.name,
            value: brand.id,
          })),
        },
        tooltip: {
          title: t('shared.products.list.filter_tooltips.brand.title'),
          content: t('shared.products.list.filter_tooltips.brand.content'),
        },
      },
      {
        label: t(`models.product.category`),
        component: MultipleValue,
        inputName: 'products.product_category_id',
        createable: false,
        lazyLoad: {
          action: (params: Record<string, unknown>) => (currentCategoryService as any).fetchList(params),
          transform: (data: { data: Array<{ id: string; name: string }> }) => data.data.map((cat) => ({
            label: cat.name,
            value: cat.id,
          })),
        },
        tooltip: {
          title: t('shared.products.list.filter_tooltips.product_category.title'),
          content: t('shared.products.list.filter_tooltips.product_category.content'),
        },
      },
      {
        label: t(`models.product.last_inbound_date`),
        component: FullDayRangePicker,
        defaultPinned: true,
        sortable: false,
        inputName: 'latest_received_dates.latest_received_at',
        tooltip: {
          title: t('shared.products.list.filter_tooltips.last_inbound_date.title'),
          content: t('shared.products.list.filter_tooltips.last_inbound_date.content'),
        },
      },
      {
        label: t(`models.product.last_outbound_date`),
        component: FullDayRangePicker,
        sortable: false,
        defaultPinned: true,
        inputName: 'latest_shipped_dates.latest_shipped_at',
        tooltip: {
          title: t('shared.products.list.filter_tooltips.last_outbound_date.title'),
          content: t('shared.products.list.filter_tooltips.last_outbound_date.content'),
        },
      },
      {
        label: t(`models.product.activated`),
        component: SelectValue,
        inputName: 'products.activated',
        defaultOptions: [
          {
            label: t(`common.enabled`),
            value: 'true',
          },
          {
            label: t(`common.disabled`),
            value: 'false',
          },
        ],
        tooltip: {
          title: t('shared.products.list.filter_tooltips.activated.title'),
          content: t('shared.products.list.filter_tooltips.activated.content'),
        },
      }
    );

    // Add default_shelf filter for operator role only
    if (role === 'operator') {
      rules.push({
        label: t(`shared.products.list.filters.default_shelf_status`),
        component: MultipleValue,
        inputName: 'products.default_shelf_id',
        createable: false,
        defaultOptions: [
          {
            label: t(`shared.products.list.filters.has_default_shelf`),
            value: 'present',
          },
          {
            label: t(`shared.products.list.filters.no_default_shelf`),
            value: 'blank',
          },
        ],
        tooltip: {
          title: t('shared.products.list.filter_tooltips.default_shelf.title'),
          content: t('shared.products.list.filter_tooltips.default_shelf.content'),
        },
      });
    }

    // 動態添加自定義欄位篩選器
    productAttributeDefinitions.forEach((def) => {
      const filterConfig: any = {
        label: def.label,
        inputName: `custom_attributes.${def.name}`,
        createable: true,
      };

      switch (def.data_type) {
        case 'select':
          filterConfig.component = MultipleValue;
          filterConfig.createable = false;
          filterConfig.defaultOptions = (def.options || []).map((opt) => ({
            label: opt,
            value: opt,
          }));
          break;
        case 'boolean':
          filterConfig.component = MultipleValue;
          filterConfig.createable = false;
          filterConfig.defaultOptions = [
            { label: t('common.yes'), value: 'true' },
            { label: t('common.no'), value: 'false' },
          ];
          break;
        case 'date':
          // Date 類型使用 MultipleValue，允許選擇多個日期
          filterConfig.component = MultipleValue;
          filterConfig.createable = true;
          break;
        default: // text, number
          filterConfig.component = MultipleValue;
          break;
      }

      rules.push(filterConfig);
    });

    return rules;
  }, [role, t, merchants, productAttributeDefinitions, hideMerchantUI])

  const actions = useMemo<BatchAction[]>(() => ([
    {
      name: `common.button.edit`,
      type: 'primary',
      requiredPermission: 'products:write',
      do: () => {
        setShowBatchEditPanel(true)
      }
    },
    {
      name: `common.button.export`,
      className: 'btn btn-primary btn-outline mb-0',
      requiredPermission: 'products:export',
      do: (selected) => executeBatchOperation({
        task: () => service.export(Array.from(selected), i18n.language),
        jobName: 'common.button.export'
      })
    },
  ] satisfies BatchAction[]).filter(action => !action.requiredPermission || can(action.requiredPermission)),
  [executeBatchOperation, setLastModified, service, i18n, can])

  return (
    <>
      <TableIndex
        columns={columns}
        searchable={{
          match: 'contains',
          searchFields: [
            t('models.product.sku'),
            t('models.product.name'),
            t('models.product.barcode'),
            t('models.product.other_barcode'),
          ],
        }}
        columnsEditable={true}
        tableId={`${role === 'operator' ? 'Operator' : 'Admin'}ProductsTable`}
        defaultQuery={{
          sorting_field: 'products.id',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        actions={actions}
        service={service}
        showPanel={showBatchEditPanel}
        onClosePanel={() => setShowBatchEditPanel(false)}
        batchEditPanel={{
          render: ({ selected, showPanel, onClose }: { selected: unknown; showPanel: boolean; onClose: () => void }) => (
            <ProductBatchEditPanel
              showPanel={showPanel}
              onClosePanel={onClose}
              selected={selected as Set<string>}
              setLastModified={setLastModified}  // 使用 ProductsList 自己的 setLastModified
              role={role}
              service={service} // NEW: Pass service
              addJob={addJob}
            />
          )
        }}
        filterHelpExample={t('shared.products.list.filter_help_example')}
        filterDocsUrl={`/docs/${role}/products/list#features`}
        testIdPrefix="product"
      />

      <ImportModal
        open={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onImport={handleModalImport}
        onDownloadTemplate={handleDownloadTemplate}
        role={role}
        merchants={merchants}
        title={t('shared.products.list.import.modal_title')}
      />
    </>
  )
}

export default ProductsList;
